import React, { useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'

import {
  GalleryInterface,
  GalleryItemInterface,
  LotGallery,
  PackageConfigurationInterface,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import CarouselHandler from '@components/carousel-handler'
import { PropertiyGallerySkeleton } from '@components/compare-property/skeleton'
import DataHandler from '@components/data-handler'

import ShortlistButton from '@pages/lot-view/shortlist-button'

import {
  LotAssetInterface,
  selectPrecinctListFromResult,
  useGetPrecinctListQuery,
} from '@api/houseAndLand'
import { selectFromResult, useGetUnitGalleryQuery } from '@api/unit-gallery'

import { formatPrice } from '@utilities/helper'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { CompassRegularSvg, InfoCircleSvg } from '@svg/react'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { FileType } from '@adUtilities/constants'
import { formatAreaNumber } from '@adUtilities/helper'
import mapPropertyGallery, {
  FloorImage,
  PropertyGallery,
} from '@adUtilities/property-gallery'
import { LotProperty, ViewLineImage } from '@adUtilities/types'

import LotSelector from './lot-selector'

export interface ComparableLotItemProps {
  lot: LotProperty
  projectIdentity: ProjectIdentity
  showInfoSession?: boolean
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  onSelectPrecinct?: (id: string) => void
}

const NO_IMAGE_OBJECT: GalleryItemInterface = {
  id: 'no-image',
  label: 'No Image',
  imageSource: '',
}

function ComparableLotItem({
  lot,
  projectIdentity,
  showInfoSession,
  comparePropertyProps,
  onSelectPrecinct,
}: ComparableLotItemProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const [showPropertySelector, setShowPropertySelector] = useState(false)

  const { checkPropertyPositionInCompareMode } = comparePropertyProps

  const lotIndex = useMemo(
    () => checkPropertyPositionInCompareMode(lot.data.id)?.index,
    [lot.data.id, checkPropertyPositionInCompareMode]
  )

  const { onlyShowPropertyGalleries, projectUUID, projectName } =
    projectIdentity

  const lotGalleryQuery = useGetUnitGalleryQuery(
    { projectName, unitId: lot.data.name },
    { selectFromResult }
  )
  const { unitGalleryData: lotGalleryData } = lotGalleryQuery

  const precinctsPayload = useGetPrecinctListQuery(
    { projectName: projectIdentity.projectId },
    {
      selectFromResult: selectPrecinctListFromResult,
    }
  )

  const transformImageAsset = (item: LotAssetInterface) => ({
    id: item.id,
    src: item.url,
    label: item.title || item.name,
    order: item.order,
  })

  const buildCarouselItems = (
    propertyGallery: PropertyGallery[]
  ): GalleryItemInterface[] =>
    propertyGallery.map(({ id, src, thumbnail, type, noSpliceUrl }) => ({
      id,
      imageSource: type === FileType.Video ? thumbnail || '' : src,
      videoSource: type === FileType.Video ? src : '',
      config: {
        noSpliceUrl,
        staticUrl: false,
      },
    }))

  const carouselData = useMemo<GalleryInterface>(() => {
    const floorItems: FloorImage[] = lot.data.assets?.floorImage
      ? [transformImageAsset(lot.data.assets.floorImage)]
      : []

    const viewLineItems: ViewLineImage[] =
      lot.data.assets?.viewLinesImage?.map(transformImageAsset) || []

    const propertyGalleries: PropertyGallery[] = (lotGalleryData || []).map(
      (item: LotGallery) => ({
        id: item.id,
        src: item.src,
        label: item.label || '',
        type: item.type,
        thumbnail: item.thumbnail,
        noSpliceUrl: false,
      })
    )

    const propertyGallery = mapPropertyGallery({
      projectId: projectIdentity.projectUUID,
      floorImages: floorItems,
      viewLineImages: viewLineItems,
      propertyGalleries,
      onlyShowPropertyGallery: onlyShowPropertyGalleries || false,
    })

    const carouselItems = buildCarouselItems(propertyGallery)

    return {
      items: carouselItems[0] ? [carouselItems[0]] : [NO_IMAGE_OBJECT],
      label: '',
    }
  }, [onlyShowPropertyGalleries, projectUUID, lot.data.assets, lotGalleryData])

  const isPriceVisible =
    projectIdentity.showPrice &&
    !(
      lot.data.status !== projectIdentity?.statusLabels?.available &&
      projectIdentity.hideSoldPrice
    )

  const activePackageConfiguration = useMemo<PackageConfigurationInterface>(
    () => ({
      floorplan: '',
      floorplanOption: '',
      facade: '',
      facadeColor: '',
      internalTheme: '',
      internalColorScheme: '',
    }),
    []
  )

  const handleToggleInfo = useCallback(async () => {
    await firebaseControlQuery.update({
      [`houseAndLand.lotInfoPopup`]: !showInfoSession,
    })
  }, [showInfoSession])

  return (
    <div className="flex h-full w-full flex-col gap-[10px]">
      <LotSelector
        lot={lot}
        lotIndex={lotIndex}
        comparePropertyProps={comparePropertyProps}
        onSelectPrecinct={onSelectPrecinct}
        onToggleList={setShowPropertySelector}
      />
      {showPropertySelector ? null : (
        <div className="flex h-full w-full flex-col gap-[15px]">
          <div className="min-h-[250px] overflow-hidden rounded-md ipad-mini-land:min-h-[300px]">
            <DataHandler
              payloads={{
                ...lotGalleryQuery,
                data: carouselData,
              }}
              skeletonFrame={<PropertiyGallerySkeleton />}
            >
              <CarouselHandler
                carousel={carouselData}
                imgClass="object-cover !h-auto"
                activeIndex={0}
                hasBlurredEffect
              />
            </DataHandler>
          </div>
          <div className="flex flex-1 flex-col justify-between rounded-lg bg-white/10 py-4 text-white ipad-mini-land:py-5">
            <div className="no-scrollbar flex-1 basis-0 overflow-y-scroll text-white">
              {precinctsPayload?.precinctList?.length > 1 && (
                <p className="px-4 text-base font-bold ipad-mini-land:px-6">
                  {lot.references.precinctId}
                </p>
              )}
              <div className="px-4 ipad-mini-land:px-6">
                <p className="text-[25px] font-bold">
                  {`Lot ${lot.data.name}`}
                </p>

                {Number(lot.data.price) > 0 && isPriceVisible && (
                  <p className="text-[25px] font-medium">
                    {formatPrice(lot.data.price)}
                  </p>
                )}
              </div>
              <div>
                {lot.data.aspect ? (
                  <div className="my-4 flex flex-1 flex-wrap justify-between gap-x-[0.6rem] gap-y-1 bg-black/25 px-4 py-2 empty:hidden ipad-mini-land:px-6 ipad-mini-land:py-4">
                    <div className="border-l border-white first:hidden" />
                    <div className="inline-flex items-center text-base font-medium only:self-start ipad-mini-land:text-lg">
                      <CompassRegularSvg
                        className="mr-1.5 h-5 w-5 ipad-mini-land:h-6 ipad-mini-land:w-6"
                        fill="white"
                        size="m"
                      />
                      {lot.data.aspect}
                    </div>
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 px-4 pt-2 text-xl ipad-mini-land:px-6">
                  {!!lot.data.area && (
                    <p>
                      Lot Area:{' '}
                      <span className="font-semibold">
                        {formatAreaNumber(lot.data.area)} sqm
                      </span>
                    </p>
                  )}
                  {!!lot.data.frontage && (
                    <p>
                      Lot Frontage:{' '}
                      <span className="font-semibold">
                        {formatAreaNumber(lot.data.frontage)} sqm
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4 px-6">
              <ShortlistButton
                activeLotData={lot.data}
                activePrecinctId={lot.references.precinctId}
                activeStageId={lot.references.stageId}
                activePackageConfiguration={activePackageConfiguration}
              />
              <button
                className="flex w-full items-center justify-center gap-4 rounded-lg border-2 border-white p-4 text-base font-bold text-white ipad-mini-land:py-4 ipad-mini-land:text-lg"
                type="button"
                onClick={handleToggleInfo}
              >
                {showInfoSession ? 'HIDE INFORMATION' : 'SHOW INFORMATION'}{' '}
                <InfoCircleSvg size="m" className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    showInfoSession: !!session?.houseAndLand?.lotInfoPopup,
    projectIdentity,
  })
)(ComparableLotItem)
